/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useEffect, useState } from 'react';
// Redux
import { useDispatch } from 'react-redux';
// Actions
import { updateInstance } from '../../actions';
import { commentCollectionsConfig } from './CommentCollectionsConfig';
// BASE Components
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
// Material UI Components
import { lighten } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Timeline from '@mui/lab/Timeline';
// Custom Components
import authRoles from '../../auth/authRoles';
import CommentCardHeader from './CommentCardHeader';
import CommentContentCard from './CommentContentCard';
import RepliesHeader from './RepliesHeader';
import ReplyItem from './ReplyItem';

function CommentCard(props) {
  const dispatch = useDispatch();
  const { comment, user, searchText, isIMI = false, resource, collection = 'comments' } = props;
  const [approved, setApproved] = useState(() => comment.approved ?? false);
  // the following is to support when the approve button is clicked to update header status
  const [headerComment, setHeaderComment] = useState(() => comment);

  const handleApproval = () => {
    let data = {
      user: user._id || user.id,
      id: comment.id,
      approved: !approved,
      status: !approved ? 'approved' : 'pending'
    };

    dispatch(updateInstance(collection)(data)).then(({ payload }) => {
      setApproved((prev) => !prev);
      setHeaderComment({
        ...comment,
        status: data.status
      });
    });
  };

  function renderReplies() {
    const internalCycles = commentCollectionsConfig[collection]?.internalCycles || [];

    const filteredReplies = comment.replies.filter(
      (reply) =>
        authRoles.editor.includes(user.role) ||
        !internalCycles.includes(reply?.review?.cycle)
    );

    if (filteredReplies.length === 0) return null;

    return (
      <Box
        className="card-footer flex flex-col p-0 border-t-1"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? lighten(theme.palette.background.default, 0.4)
              : lighten(theme.palette.background.default, 0.02)
        }}
      >
        <Timeline
          className="p-16 w-full"
          position="right"
          sx={{
            '& .MuiTimelineItem-root:before': {
              display: 'none'
            }
          }}
        >
          <RepliesHeader />
          {filteredReplies.map((reply, index) => (
            <ReplyItem
              key={reply.id}
              reply={reply}
              last={filteredReplies.length === index + 1}
              user={user}
              searchText={searchText}
              resource={resource}
              isIMI={isIMI}
              collection={collection}
            />
          ))}
        </Timeline>
      </Box>
    );
  }

  useEffect(() => {
    setApproved(comment.approved);
    setHeaderComment({
      ...comment
    });
  }, [comment]);

  return (
    <Card key={comment.id} className="mb-32 p-0 w-full border-1">
      <CommentCardHeader
        comment={headerComment}
        destination={comment.page.id}
        isIMI={isIMI}
        collection={collection}
      />
      <CardContent className="p-0">
        <CommentContentCard
          comment={comment}
          user={user}
          searchText={searchText}
          resource={resource}
          isIMI={isIMI}
          collection={collection}
        />
        {renderReplies()}
      </CardContent>
      <CardActions className="flex justify-end">
        {authRoles.leadReviewer.includes(user.role) &&
          (!approved ? (
            <Button
              variant="contained"
              color="success"
              size="small"
              aria-label="Approve"
              startIcon={
                <BASESvgIcon size={16}>heroicons-outline:check</BASESvgIcon>
              }
              className="justify-end mr-8"
              onClick={() => handleApproval()}
            >
              Approve
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              aria-label="Unapprove"
              startIcon={
                <BASESvgIcon size={16}>heroicons-outline:x</BASESvgIcon>
              }
              className="justify-end mr-8"
              onClick={() => handleApproval()}
            >
              Unapprove
            </Button>
          ))}
      </CardActions>
    </Card>
  );
}

export default CommentCard;
